// The agent picker's two decisions: what a typed term matches, and how a saved week
// reads in one line.
//
// Pure, and separate from the component, for this repository's standing reason: there
// is no DOM harness, so anything decidable without a DOM is decided here and tested by
// test/web/agent-search.test.js — the shape descriptor-form.js and on-shift.js
// established.
//
// WHY THE SCREEN CHANGED. It was a single `<select>` of every assignable user, which
// made it a lookup: a lead had to already know whose name to scroll to, and nothing
// anywhere answered "who is configured at all?". That question used to be idle
// curiosity. It is not any more — an agent with no schedule is never on shift
// (src/routing/eligibility.js), so the set of people with a week on file IS the set of
// people who can receive a ticket, and a queue whose members are all unscheduled
// assigns nothing.

import { WEEKDAY_LABELS, weeklyHours, formatHours } from '../../components/admin/schedule-coverage.js';

/** Mon, Tue… for a compact line; `WEEKDAY_LABELS` is the long form the grid uses. */
const SHORT_DAY = WEEKDAY_LABELS.map((day) => day.slice(0, 3));

/**
 * Agents whose name or email contains `term`, case-insensitively.
 *
 * EMAIL IS SEARCHED TOO, and that is not padding: this desk has people whose display
 * name is a first name and whose surname only exists in the address, so a lead typing
 * what they know — half an email — would otherwise get nothing.
 *
 * An empty term returns EVERYTHING rather than nothing. The list is rendered under a
 * search box, and a box that hides every option until you type is a box that hides the
 * fact there are options.
 */
export function matchAgents(agents, term) {
  const list = Array.isArray(agents) ? agents : [];
  const needle = String(term ?? '').trim().toLowerCase();
  if (!needle) return list;
  return list.filter((agent) => {
    const name = String(agent?.display_name ?? '').toLowerCase();
    const email = String(agent?.email ?? '').toLowerCase();
    return name.includes(needle) || email.includes(needle);
  });
}

/**
 * Consecutive weekdays that share the same hours, collapsed.
 *
 * "Mon-Fri 09:00-17:00" rather than five identical lines. A roster is read to spot the
 * one person who is different, and five repetitions of the same row per agent buries
 * that under scrolling.
 *
 * Runs Sunday-first because `agent_schedules.weekday` is 0 = Sunday (migration 013), so
 * a week worked Monday to Friday is days 1-5 and collapses cleanly. A Saturday-Sunday
 * weekend shift does NOT wrap into one range, which is correct rather than clever: they
 * are the two ends of the list, and pretending otherwise would print "Sat-Sun" for
 * someone who works two separate days.
 */
function collapseRuns(entries) {
  const out = [];
  for (const entry of entries) {
    const last = out[out.length - 1];
    if (last && last.hours === entry.hours && entry.weekday === last.to + 1) {
      last.to = entry.weekday;
      continue;
    }
    out.push({ from: entry.weekday, to: entry.weekday, hours: entry.hours });
  }
  return out;
}

/**
 * One agent's week as a single line, e.g. `Mon-Fri 09:00-17:00 · 40 hours`.
 *
 * `null` for no rows rather than an empty string, so a caller has to decide what an
 * unscheduled agent looks like instead of rendering a blank cell that reads as "fine".
 * That distinction is the whole point of the roster.
 *
 * @param {Array<{weekday: number, start_time: string, end_time: string}>} rows
 * @returns {string|null}
 */
export function scheduleSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;

  // One entry per day, with that day's shifts joined — a day can hold more than one.
  const byDay = new Map();
  for (const row of list) {
    const day = Number(row?.weekday);
    if (!Number.isInteger(day) || day < 0 || day > 6) continue;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(`${row.start_time}-${row.end_time}`);
  }
  if (byDay.size === 0) return null;

  const entries = [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([weekday, shifts]) => ({ weekday, hours: shifts.join(', ') }));

  const parts = collapseRuns(entries).map(({ from, to, hours }) =>
    (from === to ? `${SHORT_DAY[from]} ${hours}` : `${SHORT_DAY[from]}-${SHORT_DAY[to]} ${hours}`));

  return `${parts.join(', ')} · ${formatHours(weeklyHours(list))}`;
}
